import { EMarketingSpot } from '@/components/content/EMarketingSpot';
import { CATALOG_PREFIX, HEADER_DEPARTMENT_LABEL } from '@/components/content/Header/constants';
import { useHeaderPanels } from '@/components/content/Header/context/HeaderPanelsContext';
import { DepartmentsOptionMobile } from '@/components/content/Header/DepartmentsOptionMobile';
import { DepartmentsMenu } from '@/components/content/Header/parts/DepartmentsMenu';
import {
	departmentsOptionHeaderSX,
	departmentsOptionLabelHeaderSX,
	departmentsOptionMobileStylesSX,
	departmentsOptionStylesSX,
	eSpotContainerSX,
	styledBackButtonItemSX,
	styledCategoryItemSX,
	styledDepartmentItemSX,
	styledEMarketingSpotSX,
	styledSelectedDepartmentSX,
	styledSubCategoryItemSX,
} from '@/components/content/Header/styles/departmentsOption';
import DynamicIcon, { IconName } from '@/components/icons/DynamicIcon';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { compare } from '@/utils/commons';
import { toCamelCase } from '@/utils/toCamelCase';
import {
	Box,
	Collapse,
	Fade,
	IconButton,
	ListItemText,
	MenuItem,
	Typography,
	useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState, type MouseEvent } from 'react';
import {
	MATERIAL_SYMBOL_ICONS,
	OpenSansFontWeights,
} from 'shared/custom/constants/commonConstants';
import { Categories, useGetCategories } from 'shared/custom/hooks/Categories';

const INIT_CATEGORY = {
	id: '',
	url: '',
	name: '',
	label: '',
	children: [],
	uniqueID: '',
	thumbnail: '',
	identifier: '',
};

const enum DepartmentLevels {
	LEVEL1 = 1,
	LEVEL2 = 2,
	LEVEL3 = 3,
}

// eslint-disable-next-line complexity
export const DepartmentsOptionHeader = () => {
	const theme = useTheme();
	const isMobileAndTablet = useMediaQuery(theme.breakpoints.down('lg'));

	const id = 'departmentMenuPanelTrigger-desktop';
	const { activePanelId, anchorEl, togglePanel, closePanel } = useHeaderPanels();
	const isDepartmentMenuOpen = Boolean(anchorEl) && activePanelId === id;
	const [menuType, setMenuType] = useState<'department' | 'category' | 'subcategory'>();

	const { navigation } = useGetCategories();
	// const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [departmentSelected, setDepartmentSelected] = useState<Categories>(INIT_CATEGORY);
	const [categorySelected, setCategorySelected] = useState<Categories>(INIT_CATEGORY);
	const DepartmentTriggerLabels = useLocalization('DepartmentsTrigger');

	const router = useNextRouter();

	const handleOpenDepartmentMenu = (event: MouseEvent<HTMLElement>) => {
		setMenuType('department');
		togglePanel(id, event.currentTarget);
	};

	const clearData = () => {
		setDepartmentSelected(INIT_CATEGORY);
		setCategorySelected(INIT_CATEGORY);
	};

	const handleCloseDepartmentMenu = () => {
		// setAnchorEl(null);
		closePanel();
		clearData();
	};

	const showPreviousLevel = (level: DepartmentLevels) => {
		if (level === DepartmentLevels.LEVEL1) {
			setMenuType('department');
			setDepartmentSelected(INIT_CATEGORY);
		} else if (level === DepartmentLevels.LEVEL2) {
			setMenuType('category');
			setCategorySelected(INIT_CATEGORY);
		}
	};

	const getIconName = (thumbnail: string): IconName => {
		return thumbnail.substring(thumbnail.lastIndexOf('/') + 1) as IconName;
	};

	const navigateRoute = (categoryToNavigate: Categories) => {
		if (categoryToNavigate.url) {
			router.push(`${CATALOG_PREFIX}${categoryToNavigate.url}`);
		}

		handleCloseDepartmentMenu();
	};

	const sortCategories = (categories: Categories): Categories[] => {
		const toSort = categories.children || [];
		return toSort.sort((a, b) => compare(a.label.toLowerCase(), b.label.toLowerCase()));
	};

	const openCategories = (department: Categories) => {
		setDepartmentSelected(INIT_CATEGORY);
		setCategorySelected(INIT_CATEGORY);
		if (!department.children) {
			navigateRoute(department);
			return;
		}
		const departmentCategoriesSorted = {
			...department,
			children: sortCategories(department),
		};
		setDepartmentSelected(departmentCategoriesSorted);
		setMenuType('category');
	};

	const openSubCategoriesAction = (category: Categories) => {
		if (category.children.length === 0) {
			navigateRoute(category);
			return;
		}
		const subCategoriesSorted = {
			...category,
			children: sortCategories(category),
		};
		setCategorySelected(subCategoriesSorted);
		setMenuType('subcategory');
	};

	const BtnClose = () => (
		<IconButton
			onClick={handleCloseDepartmentMenu}
			aria-label="close"
			sx={departmentsOptionStylesSX.closeButton}
		>
			<DynamicIcon name={MATERIAL_SYMBOL_ICONS.close} sx={departmentsOptionStylesSX.closeIcon} />
		</IconButton>
	);

	const Departments = () => {
		const filteredDepartments = navigation.filter((item) => item.label !== HEADER_DEPARTMENT_LABEL);

		return (
			<Box sx={departmentsOptionMobileStylesSX.menuWidth(theme)}>
				{isMobileAndTablet ? (
					<Box sx={styledDepartmentItemSX(theme)}>
						<DynamicIcon
							name={MATERIAL_SYMBOL_ICONS.department}
							color={theme.palette.primary.main}
							variant="filled"
							style={{ zIndex: 3000 }}
						/>
						<ListItemText
							primary={DepartmentTriggerLabels.departments.t()}
							disableTypography
							sx={departmentsOptionStylesSX.listItemText}
						/>
						<BtnClose />
					</Box>
				) : null}
				{filteredDepartments.map((department) => (
					<MenuItem
						key={department.id}
						sx={styledDepartmentItemSX(theme)}
						onClick={() => openCategories(department)}
						data-name="menu-lvl1"
						data-content={`dep-${department.identifier}`}
						id={`department-${department.id}`}
						disableRipple
					>
						{department?.thumbnail && (
							<DynamicIcon
								name={MATERIAL_SYMBOL_ICONS[getIconName(toCamelCase(department.thumbnail))]}
							/>
						)}
						<Typography fontWeight={OpenSansFontWeights.semibold}>{department.label}</Typography>
					</MenuItem>
				))}
			</Box>
		);
	};

	const Categories = () => (
		<Box sx={departmentsOptionMobileStylesSX.categoriesMenuWidth(theme)}>
			{menuType === 'category' && departmentSelected?.label ? (
				<>
					<Box sx={styledBackButtonItemSX(theme)}>
						{isMobileAndTablet ? (
							<DynamicIcon
								name={MATERIAL_SYMBOL_ICONS.department}
								variant="filled"
								color={theme.palette.primary.main}
							/>
						) : null}
						<DynamicIcon name={MATERIAL_SYMBOL_ICONS.keyboardArrowLeft} />
						<ListItemText
							primary={DepartmentTriggerLabels.goBack.t()}
							disableTypography
							onClick={() => showPreviousLevel(DepartmentLevels.LEVEL1)}
							sx={departmentsOptionStylesSX.goBackText}
						/>
						{isMobileAndTablet ? <BtnClose /> : null}
					</Box>
					<Box sx={styledSelectedDepartmentSX(theme)}>
						{!isMobileAndTablet && departmentSelected?.thumbnail ? (
							<DynamicIcon
								name={MATERIAL_SYMBOL_ICONS[getIconName(toCamelCase(departmentSelected.thumbnail))]}
								variant="filled"
								color={theme.palette.primary.main}
							/>
						) : null}
						<Typography sx={departmentsOptionStylesSX.categoriesItemText}>
							{departmentSelected.label}
						</Typography>
					</Box>
					<Box sx={styledCategoryItemSX(theme)} onClick={() => navigateRoute(departmentSelected)}>
						<ListItemText
							primary={DepartmentTriggerLabels.viewDepartment.t()}
							disableTypography
							sx={departmentsOptionStylesSX.viewCategory}
						/>
					</Box>
					{isMobileAndTablet && departmentSelected?.label !== '' ? (
						<Box sx={styledEMarketingSpotSX}>
							<EMarketingSpot
								id={`Menu_departmentSidebar_${departmentSelected.identifier}`}
								properties={{
									emsName: `Menu_departmentSidebar_${departmentSelected.identifier}`,
								}}
							/>
						</Box>
					) : null}
					{departmentSelected.children?.map((category: Categories) => {
						const categoryChildrenIcon =
							category?.children.length > 0
								? MATERIAL_SYMBOL_ICONS.login
								: MATERIAL_SYMBOL_ICONS.keyboardArrowRight;

						return (
							<MenuItem
								id={`categoryLink_${category.id}_${departmentSelected.id}`}
								key={category.id}
								sx={styledCategoryItemSX(theme)}
								onClick={() => openSubCategoriesAction(category)}
								data-name="menu-lvl2"
								data-content={`dep-${category.identifier}`}
								disableRipple
							>
								<ListItemText
									primary={category.label}
									disableTypography
									sx={departmentsOptionStylesSX.ListItemText}
								/>
								<DynamicIcon name={categoryChildrenIcon} />
							</MenuItem>
						);
					})}
				</>
			) : null}
		</Box>
	);

	const Subcategories = () => (
		<Box sx={departmentsOptionMobileStylesSX.menuWidth(theme)}>
			{menuType === 'subcategory' && categorySelected?.label ? (
				<>
					<Box sx={styledBackButtonItemSX(theme)}>
						{isMobileAndTablet && departmentSelected?.thumbnail ? (
							<DynamicIcon
								name={MATERIAL_SYMBOL_ICONS[getIconName(toCamelCase(departmentSelected.thumbnail))]}
								variant="filled"
								sx={departmentsOptionStylesSX.previousLevelIcon}
							/>
						) : null}
						<DynamicIcon name={MATERIAL_SYMBOL_ICONS.keyboardArrowLeft} />
						<Typography
							sx={departmentsOptionStylesSX.previousLevelText}
							onClick={() => showPreviousLevel(DepartmentLevels.LEVEL2)}
						>
							{departmentSelected?.label || DepartmentTriggerLabels.goBack.t()}
						</Typography>
						{isMobileAndTablet ? <BtnClose /> : null}
					</Box>
					<Box sx={styledSelectedDepartmentSX(theme)}>
						{!isMobileAndTablet && departmentSelected?.thumbnail ? (
							<DynamicIcon
								name={MATERIAL_SYMBOL_ICONS[getIconName(toCamelCase(departmentSelected.thumbnail))]}
								variant="filled"
							/>
						) : null}
						<Typography sx={departmentsOptionStylesSX.categoriesItemText}>
							{categorySelected?.label}
						</Typography>
					</Box>
					<MenuItem
						sx={styledCategoryItemSX(theme)}
						onClick={() => navigateRoute(categorySelected)}
						disableRipple
					>
						<ListItemText disableTypography sx={departmentsOptionStylesSX.viewCategory}>
							{DepartmentTriggerLabels.ViewCategory.t()}
						</ListItemText>
					</MenuItem>
					{categorySelected.children?.map((subCategory: Categories) => (
						<MenuItem
							id={`subcategoryLink_${departmentSelected.id}_${categorySelected.id}_${subCategory.id}`}
							key={subCategory.id}
							data-name="menu-lvl3"
							data-content={`dep-${subCategory.identifier}`}
							sx={styledSubCategoryItemSX(theme)}
							onClick={() => navigateRoute(subCategory)}
							disableRipple
						>
							<DynamicIcon name={MATERIAL_SYMBOL_ICONS.subdirectoryArrowRight} />
							<ListItemText
								primary={subCategory.label}
								disableTypography
								sx={departmentsOptionStylesSX.ListItemText}
							/>
						</MenuItem>
					))}
				</>
			) : null}
		</Box>
	);

	/* useEffect(() => {
		handleCloseDepartmentMenu();
	}, [isMobileAndTablet]); */

	return (
		<>
			<Box
				sx={departmentsOptionHeaderSX(theme, isDepartmentMenuOpen)}
				data-testid="departments-container"
				onClick={handleOpenDepartmentMenu}
				id={id}
			>
				<Box>
					<DynamicIcon
						variant={isDepartmentMenuOpen ? 'filled' : 'rounded'}
						name={MATERIAL_SYMBOL_ICONS.department}
						color={isDepartmentMenuOpen ? theme.palette.primary.main : theme.palette.grey[500]}
					/>
				</Box>
				<Typography variant="bodySubText" sx={departmentsOptionLabelHeaderSX(theme)}>
					{DepartmentTriggerLabels.departments.t()}
				</Typography>
			</Box>

			<DepartmentsMenu
				name="departments"
				open={!isMobileAndTablet && isDepartmentMenuOpen && menuType === 'department'}
				anchorEl={anchorEl}
				handleClose={handleCloseDepartmentMenu}
				Transition={Fade}
			>
				<Departments />
			</DepartmentsMenu>

			{isDepartmentMenuOpen && menuType === 'category' ? (
				<DepartmentsMenu
					name="categories"
					anchorEl={anchorEl}
					open={!isMobileAndTablet && isDepartmentMenuOpen && menuType === 'category'}
					handleClose={handleCloseDepartmentMenu}
					Transition={Collapse}
				>
					<Box sx={departmentsOptionStylesSX.eSpotContainer}>
						<Categories />
						{departmentSelected.label !== '' ? (
							<Box sx={eSpotContainerSX}>
								<EMarketingSpot
									id={`Menu_departmentSidebar_${departmentSelected.identifier}`}
									properties={{
										emsName: `Menu_departmentSidebar_${departmentSelected.identifier}`,
									}}
								/>
							</Box>
						) : null}
					</Box>
				</DepartmentsMenu>
			) : null}

			{isDepartmentMenuOpen && menuType === 'subcategory' ? (
				<DepartmentsMenu
					name="sub-categories"
					anchorEl={anchorEl}
					open={!isMobileAndTablet && isDepartmentMenuOpen && menuType === 'subcategory'}
					handleClose={handleCloseDepartmentMenu}
					Transition={Collapse}
				>
					<Subcategories />
				</DepartmentsMenu>
			) : null}

			{/* Mobile Drawers */}
			{isMobileAndTablet ? (
				<>
					<DepartmentsOptionMobile
						isOpen={isDepartmentMenuOpen && menuType === 'department'}
						handleClose={handleCloseDepartmentMenu}
						openDrawer={handleOpenDepartmentMenu}
					>
						<Departments />
					</DepartmentsOptionMobile>
					<DepartmentsOptionMobile
						isOpen={isDepartmentMenuOpen && menuType === 'category'}
						handleClose={handleCloseDepartmentMenu}
						openDrawer={handleOpenDepartmentMenu}
					>
						<Categories />
					</DepartmentsOptionMobile>
					<DepartmentsOptionMobile
						isOpen={isDepartmentMenuOpen && menuType === 'subcategory'}
						handleClose={handleCloseDepartmentMenu}
						openDrawer={handleOpenDepartmentMenu}
					>
						<Subcategories />
					</DepartmentsOptionMobile>
				</>
			) : null}
		</>
	);
};
